import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import { 
  Check, 
  Users, 
  Building2, 
  CreditCard,
  Minus,
  Plus,
  ShoppingCart,
  Mail,
  Lock,
  User,
  Eye,
  EyeOff,
  Gift,
  Globe,
  CheckCircle,
  XCircle,
  Loader2,
  Zap,
  Clock,
  ArrowLeft,
  Palette,
  Star,
  ShieldCheck,
  Tag,
  X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrustBadges = ({ t }) => (
  <div className="space-y-4 pt-2" data-testid="trust-badges">
    {/* SSL + Stripe */}
    <div className="flex items-center gap-3 p-3 rounded-xl bg-green-50 border border-green-100">
      <ShieldCheck className="w-5 h-5 text-green-600 flex-shrink-0" />
      <div className="min-w-0">
        <p className="text-sm font-medium text-green-800">{t('trustBadges.securePayment')}</p>
        <p className="text-xs text-green-600">{t('trustBadges.encryptedInfo')}</p>
      </div>
    </div>

    {/* Powered by Stripe */}
    <div className="flex items-center justify-between p-3 rounded-xl bg-slate-50 border border-slate-100">
      <div className="flex items-center gap-2">
        <svg viewBox="0 0 60 25" className="h-6 w-auto" aria-label="Stripe">
          <path fill="#635BFF" d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a12.3 12.3 0 0 1-4.56.85c-4.14 0-6.6-2.55-6.6-6.76 0-3.76 2.18-6.9 6.04-6.9 3.56 0 5.97 2.74 5.97 6.76 0 .39-.02.78-.04 1.13zm-4.08-5.57c-1.26 0-2.1.95-2.2 2.5h4.37c-.05-1.55-.87-2.5-2.17-2.5zM40.95 20.3c-1.44 0-2.32-.64-2.9-1.27l-.02 5.63-3.89.83V6.66h3.33l.16 1.18c.64-.82 1.74-1.45 3.16-1.45 2.86 0 5.07 2.58 5.07 6.73 0 4.6-2.15 7.18-4.91 7.18zm-.8-10.33c-1 0-1.63.47-2.06 1.08l.02 5.73c.4.56 1.03 1.06 2.04 1.06 1.57 0 2.63-1.73 2.63-3.97 0-2.18-1.08-3.9-2.63-3.9zM28.24 5.57c-1.34 0-2.17-.88-2.17-1.97 0-1.12.86-1.97 2.2-1.97 1.34 0 2.17.85 2.17 1.97 0 1.09-.86 1.97-2.2 1.97zm-1.93 14.44V6.66h3.89V20h-3.89zm-5.2-3.4c0 1.18.65 1.62 1.63 1.62.56 0 1.2-.15 1.79-.45v3.17c-.72.37-1.74.62-2.87.62-2.67 0-4.44-1.37-4.44-4.56V9.91h-1.87V6.66h1.87l.52-3.72h3.37v3.72h3.18v3.25h-3.18v6.7zM9.46 7.4c1.55 0 2.64.32 3.44.72V4.88C12.04 4.53 11 4.17 9.56 4.17c-3.2 0-5.43 1.67-5.43 4.47 0 4.37 6.01 3.67 6.01 5.55 0 .97-.86 1.29-2.04 1.29-1.44 0-3.12-.64-4.34-1.36v3.4c1.3.64 2.67 1 4.23 1 3.32 0 5.6-1.6 5.6-4.47-.03-4.72-6.13-3.87-6.13-5.65z"/>
        </svg>
        <span className="text-xs text-slate-500">{t('trustBadges.poweredByStripe')}</span>
      </div>
      <Lock className="w-4 h-4 text-slate-400" />
    </div>

    {/* Accepted Payment Methods */}
    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
      <p className="text-xs text-slate-500 mb-2.5 font-medium">{t('trustBadges.acceptedPayments')}</p>
      <div className="flex items-center gap-2.5">
        {/* Visa */}
        <div className="h-7 px-2 bg-white rounded border border-slate-200 flex items-center" title="Visa">
          <svg viewBox="0 0 48 32" className="h-5 w-auto">
            <rect width="48" height="32" rx="4" fill="#fff"/>
            <path d="M19.5 21.5h-3.3l2.1-12.7h3.3l-2.1 12.7zm13.7-12.4c-.7-.3-1.7-.5-3-.5-3.3 0-5.6 1.7-5.6 4.2 0 1.8 1.7 2.8 2.9 3.4 1.3.6 1.7 1 1.7 1.6 0 .8-1 1.2-2 1.2-1.3 0-2-.2-3.1-.7l-.4-.2-.5 2.8c.8.3 2.2.6 3.7.6 3.5 0 5.7-1.7 5.8-4.3 0-1.4-.9-2.5-2.8-3.4-1.2-.6-1.9-1-1.9-1.6 0-.5.6-1.1 1.9-1.1 1.1 0 1.9.2 2.5.5l.3.1.5-2.6zm8.5.3h-2.6c-.8 0-1.4.2-1.8 1l-5 11.6h3.5l.7-1.9h4.3l.4 1.9h3.1l-2.6-12.6zm-4.1 8.2l1.8-4.7.5 2.3.5 2.4h-2.8zM17.2 8.8l-3.1 8.7-.3-1.7c-.6-1.9-2.4-3.9-4.4-5l3 11h3.5l5.3-13h-4z" fill="#1A1F71"/>
            <path d="M12.1 8.8H7l-.1.3c4.2 1 7 3.5 8.1 6.5l-1.2-5.8c-.2-.8-.8-1-1.7-1z" fill="#F7A600"/>
          </svg>
        </div>
        {/* Mastercard */}
        <div className="h-7 px-2 bg-white rounded border border-slate-200 flex items-center" title="Mastercard">
          <svg viewBox="0 0 48 32" className="h-5 w-auto">
            <rect width="48" height="32" rx="4" fill="#fff"/>
            <circle cx="19" cy="16" r="8" fill="#EB001B"/>
            <circle cx="29" cy="16" r="8" fill="#F79E1B"/>
            <path d="M24 10.3a8 8 0 0 1 0 11.4 8 8 0 0 1 0-11.4z" fill="#FF5F00"/>
          </svg>
        </div>
        {/* Amex */}
        <div className="h-7 px-2 bg-white rounded border border-slate-200 flex items-center" title="American Express">
          <svg viewBox="0 0 48 32" className="h-5 w-auto">
            <rect width="48" height="32" rx="4" fill="#016FD0"/>
            <path d="M24 22.5v-3h6.3l.7-1 .7 1H38v-7H31.7l-.7 1-.7-1H15v7h2.4l.5-1.2h1.1l.5 1.2h4.5zm-5.7-5.5l-2 4.5h1.4l.4-.9h2.1l.4.9h1.4l-2-4.5h-1.7zm.6 1.1l.6 1.4h-1.3l.7-1.4zm5.3-.1v4.5h-1.2v-2.8l-1.5 2.8h-1l-1.5-2.8V22h-1.2v-4.5h1.4l1.3 2.6 1.3-2.6h1.4zm5.5 0h-3.5V22h3.5v-1h-2.3v-.8h2.2v-1h-2.2v-.8h2.3v-1zm2.5 0l-1.7 2 1.8 2.5h-1.5l-1.1-1.6-1.1 1.6h-1.4l1.7-2.3-1.6-2.2h1.5l1 1.4 1-1.4h1.4z" fill="#fff"/>
          </svg>
        </div>
        {/* PayPal (via Stripe) */}
        <div className="h-7 px-2 bg-white rounded border border-slate-200 flex items-center" title="PayPal">
          <svg viewBox="0 0 48 32" className="h-5 w-auto">
            <rect width="48" height="32" rx="4" fill="#fff"/>
            <path d="M33.2 10.5c.9 1 1.3 2.4 1.1 4.2-.7 4.3-3.5 6.3-8 6.3h-.8c-.5 0-.9.3-1 .8l-.7 4.2c-.1.4-.4.7-.8.7h-3.3c-.4 0-.6-.3-.5-.7l.1-.5 2.5-15.5c.1-.5.5-.8 1-.8h5.3c2.1 0 3.7.5 4.6 1.5l.5-.2z" fill="#253B80"/>
            <path d="M34.3 14.7c-.7 4.5-3.7 6.3-8.2 6.3h-1c-.5 0-.9.4-1 .8l-.9 5.5c-.1.4.2.7.5.7h3.3c.4 0 .8-.3.9-.7l.6-3.8c.1-.4.5-.8 1-.8h.6c3.9 0 6.4-1.6 7-6 .3-1.6-.1-3-.9-3.9-.2 0-.2 0 0 0l1.1-1.1c.8 1 1.3 2.4 1.1 4l-4.1-.1v.1z" fill="#179BD7"/>
          </svg>
        </div>
      </div>
    </div>

    {/* Data Protection */}
    <div className="flex items-center gap-2 px-1">
      <Lock className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
      <p className="text-xs text-slate-400">{t('trustBadges.dataProtection')}</p>
    </div>
  </div>
);

const productIcons = {
  brand: CreditCard,
  custom: Palette,
  enterprise: Building2,
  haiti_mondial: Globe
};

const colorOptions = {
  white: { label: 'Blanc', color: 'bg-white border border-slate-300' },
  black: { label: 'Noir', color: 'bg-slate-900' },
  blue: { label: 'Bleu', color: 'bg-blue-600' },
  pink: { label: 'Rose', color: 'bg-pink-500' },
  custom: { label: 'Personnalisé', color: 'bg-gradient-to-r from-red-400 via-yellow-400 to-blue-400' }
};

const accentColors = {
  brand: 'from-[#0A2540] to-[#0A2540]/80',
  custom: 'from-violet-600 to-purple-500',
  enterprise: 'from-amber-500 to-orange-500',
  haiti_mondial: 'from-emerald-600 to-teal-500'
};

export default function ProductDetail() {
  const { productType } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedColor, setSelectedColor] = useState('');
  const [businessSigle, setBusinessSigle] = useState('');
  const [planChoice, setPlanChoice] = useState('pro');
  const [orderCalculation, setOrderCalculation] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);

  // Payment mode state
  const [paymentMode, setPaymentMode] = useState('stripe');

  // Sigle validation state
  const [sigleValidation, setSigleValidation] = useState({ status: 'idle', message: '' });
  const [sigleDebounceTimer, setSigleDebounceTimer] = useState(null);

  // Checkout form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Coupon state
  const [couponCode, setCouponCode] = useState('');
  const [couponApplying, setCouponApplying] = useState(false);
  const [couponResult, setCouponResult] = useState(null); // { valid, discount_amount, message, ... }

  useEffect(() => {
    fetchProduct();
  }, [productType]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProduct = async () => {
    try {
      const response = await fetch(`${API}/products/${productType}`);
      if (response.ok) {
        const data = await response.json();
        setProduct(data);
        setQuantity(data.min_quantity);
        if (data.colors?.length > 0) {
          setSelectedColor(data.colors[0]);
        }
        if (data.plan_type === 'choice') {
          setPlanChoice('pro');
        }
      } else {
        navigate('/products');
      }
    } catch (error) {
      console.error('Failed to fetch product:', error);
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (product) {
      calculateOrder();
    }
  }, [quantity, planChoice, product]); // eslint-disable-line react-hooks/exhaustive-deps

  const calculateOrder = async () => {
    if (!product) return;
    try {
      const body = {
        product_type: product.product_id.replace('card_', ''),
        quantity: quantity,
        plan_choice: product.plan_type === 'choice' ? planChoice : null
      };
      const response = await fetch(`${API}/checkout/calculate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (response.ok) {
        const data = await response.json();
        setOrderCalculation(data);
      }
    } catch (error) {
      console.error('Failed to calculate order:', error);
    }
  };

  // Real-time sigle validation
  const validateSigle = useCallback(async (value) => {
    if (!value || value.length < 2) {
      setSigleValidation({ status: 'idle', message: '' });
      return;
    }
    setSigleValidation({ status: 'checking', message: t('products.checkingSigle') });
    try {
      const response = await fetch(`${API}/validate/sigle?value=${encodeURIComponent(value)}`);
      if (response.ok) {
        const data = await response.json();
        setSigleValidation({
          status: data.available ? 'available' : 'taken',
          message: data.message,
          normalizedSigle: data.sigle
        });
      }
    } catch (error) {
      setSigleValidation({ status: 'error', message: t('errors.serverError') });
    }
  }, [t]);

  const handleSigleChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setBusinessSigle(value);
    if (sigleDebounceTimer) clearTimeout(sigleDebounceTimer);
    const timer = setTimeout(() => validateSigle(value), 500);
    setSigleDebounceTimer(timer);
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponApplying(true);
    try {
      const params = new URLSearchParams({
        code: couponCode.trim(),
        product_type: product.product_id.replace('card_', ''),
        plan_type: product.plan_type === 'choice' ? planChoice : 'pro',
        payment_mode: paymentMode
      });
      const response = await fetch(`${API}/coupons/validate?${params}`, { method: 'POST' });
      const data = await response.json();
      if (data.valid) {
        setCouponResult(data);
        toast.success(t('checkout.couponApplied'));
      } else {
        setCouponResult({ valid: false, message: data.message || t('checkout.couponInvalid') });
        toast.error(data.message || t('checkout.couponInvalid'));
      }
    } catch {
      toast.error(t('errors.serverError'));
    } finally {
      setCouponApplying(false);
    }
  };

  const removeCoupon = () => {
    setCouponCode('');
    setCouponResult(null);
  };

  const getDiscountedTotal = () => {
    if (!orderCalculation) return 0;
    if (!couponResult?.valid) return orderCalculation.total;
    const coupon = couponResult.coupon;
    if (coupon.discount_type === 'percentage') {
      return Math.max(0, orderCalculation.total * (1 - coupon.discount_value / 100));
    }
    return Math.max(0, orderCalculation.total - coupon.discount_value);
  };

  const getDiscountAmount = () => {
    if (!orderCalculation || !couponResult?.valid) return 0;
    return orderCalculation.total - getDiscountedTotal();
  };

  const needsSigle = () => {
    if (!product) return false;
    const pt = product.product_id.replace('card_', '');
    return pt === 'enterprise' || (pt === 'haiti_mondial' && planChoice === 'business');
  };

  const isSigleValid = () => {
    if (!needsSigle()) return true;
    return sigleValidation.status === 'available';
  };

  const validateForm = () => {
    const newErrors = {};
    if (!email) newErrors.email = t('errors.emailRequired');
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = t('errors.invalidEmail');
    if (!password) newErrors.password = t('errors.passwordRequired');
    else if (password.length < 6) newErrors.password = t('errors.passwordLength');
    if (!confirmPassword) newErrors.confirmPassword = t('auth.confirmPasswordRequired');
    else if (password !== confirmPassword) newErrors.confirmPassword = t('auth.passwordsMismatch');
    if (!firstName) newErrors.firstName = t('errors.firstNameRequired');
    if (!lastName) newErrors.lastName = t('errors.lastNameRequired');
    if (needsSigle() && !businessSigle) newErrors.sigle = t('errors.sigleRequired');
    if (needsSigle() && sigleValidation.status !== 'available') newErrors.sigle = sigleValidation.message || t('errors.sigleNotAvailable');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);

    try {
      if (paymentMode === 'stripe') {
        const stripeResponse = await fetch(`${API}/checkout/stripe/guest-session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email, password, first_name: firstName, last_name: lastName,
            product_type: product.product_id.replace('card_', ''),
            quantity, color: selectedColor || null,
            business_sigle: businessSigle || null,
            plan_choice: product.plan_type === 'choice' ? planChoice : null,
            origin_url: window.location.origin,
            coupon_code: couponResult?.valid ? couponCode.trim() : null
          })
        });
        if (stripeResponse.ok) {
          const stripeData = await stripeResponse.json();
          window.location.href = stripeData.checkout_url;
          return;
        } else {
          const error = await stripeResponse.json();
          toast.error(error.detail || t('errors.orderFailed'));
          setSubmitting(false);
          return;
        }
      }

      // MANUAL MODE
      const params = new URLSearchParams({ email, password, first_name: firstName, last_name: lastName });
      const response = await fetch(`${API}/checkout/order?${params.toString()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          product_type: product.product_id.replace('card_', ''),
          quantity, color: selectedColor,
          business_sigle: businessSigle || null,
          plan_choice: product.plan_type === 'choice' ? planChoice : null,
          payment_mode: 'manual',
          coupon_code: couponResult?.valid ? couponCode.trim() : null
        })
      });

      if (response.ok) {
        const data = await response.json();
        if (data.access_token) localStorage.setItem('token', data.access_token);
        toast.success(t('products.orderPending'));
        navigate('/checkout/pending', {
          state: {
            orderId: data.order_id, profiles: data.profiles,
            total: data.total, email, message: data.message
          }
        });
      } else {
        const error = await response.json();
        toast.error(error.detail || t('errors.orderFailed'));
      }
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleQuantityChange = (delta) => {
    if (!product) return;
    const newQty = quantity + delta;
    const minQty = (product.plan_type === 'choice' && planChoice === 'business')
      ? product.business_min_quantity
      : product.min_quantity;
    if (newQty >= minQty && newQty <= product.max_quantity) setQuantity(newQty);
  };

  const handlePlanChange = (plan) => {
    setPlanChoice(plan);
    if (plan === 'business' && product?.business_min_quantity && quantity < product.business_min_quantity) {
      setQuantity(product.business_min_quantity);
    }
  };

  const getCurrentProductImage = () => {
    if (!product) return null;
    if (product.color_images && selectedColor) return product.color_images[selectedColor] || product.image;
    return product.image;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00C48C] animate-spin" />
      </div>
    );
  }

  if (!product) return null;

  const pt = product.product_id.replace('card_', '');
  const Icon = productIcons[pt] || CreditCard;
  const accent = accentColors[pt] || accentColors.brand;
  // Use translated product data
  const td = t(`productData.${pt}`, { returnObjects: true });
  const productName = td?.name || product.name;
  const productDesc = td?.description || product.description;
  const productFeatures = Array.isArray(td?.features) ? td.features : (product.features || []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50/30 pt-24 pb-16">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Back Button */}
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/products')}
            className="rounded-full text-slate-600 hover:text-slate-900"
            data-testid="back-to-products"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('productDetail.backToProducts')}
          </Button>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-10">
          {/* Left: Product Info (3 cols) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-3 space-y-8"
          >
            {/* Product Hero */}
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
              <div className={`h-2 bg-gradient-to-r ${accent}`} />
              <div className="p-8">
                <div className="flex flex-col md:flex-row items-start gap-8">
                  {/* Image */}
                  <div className="flex-shrink-0 mx-auto md:mx-0">
                    <div className="relative w-56 h-56 flex items-center justify-center">
                      <div className={`absolute inset-0 bg-gradient-to-br ${accent} opacity-5 rounded-3xl`} />
                      <AnimatePresence mode="wait">
                        <motion.img
                          key={getCurrentProductImage()}
                          src={getCurrentProductImage()}
                          alt={product.name}
                          className="relative w-48 h-48 object-contain"
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          transition={{ duration: 0.3 }}
                        />
                      </AnimatePresence>
                    </div>
                    {/* Color Selection — right under the image */}
                    {product.colors?.length > 0 && (
                      <div className="mt-4">
                        <h3 className="text-sm font-semibold text-slate-700 mb-2 text-center">
                          {t('products.selectColor')}
                        </h3>
                        <div className="flex gap-3 justify-center">
                          {product.colors.map((color) => (
                            <button
                              key={color}
                              onClick={() => setSelectedColor(color)}
                              className={`flex flex-col items-center gap-1.5 p-2 rounded-xl transition-all ${
                                selectedColor === color
                                  ? 'ring-2 ring-blue-600 bg-[#0A2540]/5'
                                  : 'hover:bg-slate-50'
                              }`}
                              data-testid={`color-${color}`}
                            >
                              <div className={`w-10 h-10 rounded-full ${colorOptions[color]?.color || 'bg-gray-300'} shadow-md`} />
                              <span className="text-xs font-medium text-slate-600">{colorOptions[color]?.label || color}</span>
                              {selectedColor === color && <Check className="w-3.5 h-3.5 text-[#00C48C]" />}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${accent} flex items-center justify-center`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                        {product.subscription_label} {t('productsCatalog.subscription')}
                      </span>
                    </div>
                    <h1 className="font-heading text-3xl font-bold text-slate-900 mb-3" data-testid="product-detail-name">
                      {productName}
                    </h1>
                    <p className="text-slate-500 text-base leading-relaxed mb-4">
                      {productDesc}
                    </p>

                    {/* Badges */}
                    <div className="flex flex-wrap gap-2">
                      {product.badge && (
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r ${accent} text-white text-xs font-semibold`}>
                          <Star className="w-3 h-3" />
                          {product.badge}
                        </span>
                      )}
                      {product.first_year_free && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold">
                          <Gift className="w-3 h-3" />
                          {t('productsCatalog.firstYearFree')}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Features */}
            {productFeatures.length > 0 && (
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8">
                <h2 className="font-heading text-lg font-semibold text-slate-900 mb-5">
                  {t('productDetail.featuresIncluded')}
                </h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {productFeatures.map((feat, i) => (
                    <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-slate-50">
                      <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${accent} flex items-center justify-center flex-shrink-0`}>
                        <Check className="w-3.5 h-3.5 text-white" />
                      </div>
                      <span className="text-sm text-slate-700">{feat}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Plan Choice for Haiti Mondial */}
            {product.plan_type === 'choice' && (
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-8">
                <h2 className="font-heading text-lg font-semibold text-slate-900 mb-5">
                  {t('products.selectPlan')}
                </h2>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => handlePlanChange('pro')}
                    className={`p-5 rounded-2xl border-2 transition-all ${
                      planChoice === 'pro' ? 'border-[#00C48C] bg-[#00C48C]/5' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="plan-pro"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-5 h-5 text-[#0A2540]" />
                      <span className="font-semibold text-slate-900">PRO</span>
                    </div>
                    <p className="text-sm text-slate-600">$2.5/{t('productDetail.month')} ($30/{t('productDetail.year')})</p>
                    {planChoice === 'pro' && <Check className="w-4 h-4 text-[#00C48C] mt-2" />}
                  </button>
                  <button
                    onClick={() => handlePlanChange('business')}
                    className={`p-5 rounded-2xl border-2 transition-all ${
                      planChoice === 'business' ? 'border-purple-600 bg-purple-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="plan-business"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="w-5 h-5 text-purple-600" />
                      <span className="font-semibold text-slate-900">BUSINESS</span>
                    </div>
                    <p className="text-sm text-slate-600">$4/{t('productDetail.month')} ($48/{t('productDetail.year')})</p>
                    <p className="text-xs text-amber-600 mt-1">{t('products.businessMinQuantity')}</p>
                    {planChoice === 'business' && <Check className="w-4 h-4 text-purple-600 mt-2" />}
                  </button>
                </div>
              </div>
            )}
          </motion.div>

          {/* Right: Order Panel (2 cols) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <div className="sticky top-24 space-y-6">
              {/* Quantity */}
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6">
                <h3 className="font-heading text-base font-semibold text-slate-900 mb-4">
                  {t('products.quantity')}
                </h3>
                <div className="flex items-center justify-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= (product.plan_type === 'choice' && planChoice === 'business'
                      ? product.business_min_quantity : product.min_quantity)}
                    className="rounded-full"
                    data-testid="qty-minus"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="font-heading text-3xl font-bold text-slate-900 w-16 text-center">{quantity}</span>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => handleQuantityChange(1)}
                    disabled={quantity >= product.max_quantity}
                    className="rounded-full"
                    data-testid="qty-plus"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-center text-sm text-slate-500 mt-2">
                  {product.plan_type === 'choice' && planChoice === 'business'
                    ? `Min: ${product.business_min_quantity} cards`
                    : `Min: ${product.min_quantity}, Max: ${product.max_quantity}`}
                </p>
              </div>

              {/* Sigle Input */}
              {needsSigle() && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6"
                >
                  <h3 className="font-heading text-base font-semibold text-slate-900 mb-4">
                    {t('products.sigleLabel')}
                  </h3>
                  <div className="space-y-3">
                    <div className="relative">
                      <Input
                        type="text"
                        value={businessSigle}
                        onChange={handleSigleChange}
                        placeholder="Ex: TECHCORP"
                        maxLength={8}
                        className={`uppercase font-mono text-lg pr-10 ${
                          sigleValidation.status === 'available' ? 'border-green-500' :
                          sigleValidation.status === 'taken' ? 'border-red-500' : ''
                        }`}
                        data-testid="sigle-input"
                      />
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {sigleValidation.status === 'checking' && <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />}
                        {sigleValidation.status === 'available' && <CheckCircle className="w-5 h-5 text-green-500" />}
                        {sigleValidation.status === 'taken' && <XCircle className="w-5 h-5 text-red-500" />}
                      </div>
                    </div>
                    {sigleValidation.message && (
                      <p className={`text-sm ${
                        sigleValidation.status === 'available' ? 'text-green-600' :
                        sigleValidation.status === 'taken' ? 'text-red-600' : 'text-slate-500'
                      }`}>
                        {sigleValidation.message}
                      </p>
                    )}
                    <p className="text-xs text-slate-500">{t('products.sigleHelp')}</p>
                  </div>
                </motion.div>
              )}

              {/* Order Summary */}
              {orderCalculation && (
                <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6">
                  <h3 className="font-heading text-base font-semibold text-slate-900 mb-4">
                    {t('products.orderSummary')}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm text-slate-600">
                      <span>{t('products.cards')} ({quantity})</span>
                      <span>${orderCalculation.card_total?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-slate-600">
                      <span>{t('products.annualPlan')}</span>
                      {orderCalculation.first_year_free ? (
                        <div className="text-right">
                          <span className="line-through text-slate-400">${orderCalculation.renewal_price_annual?.toFixed(2)}</span>
                          <span className="ml-2 text-green-600 font-medium">$0.00</span>
                        </div>
                      ) : (
                        <span>${orderCalculation.plan_total?.toFixed(2)}</span>
                      )}
                    </div>
                    {orderCalculation.first_year_free && (
                      <div className="bg-green-50 border border-green-200 rounded-xl p-3 mt-2">
                        <div className="flex items-center gap-2 text-green-700">
                          <Gift className="w-4 h-4" />
                          <span className="text-sm font-medium">{t('products.firstYearFreeMessage')}</span>
                        </div>
                        <p className="text-xs text-green-600 mt-1">
                          {t('products.renewalNote')}: ${orderCalculation.renewal_price_annual?.toFixed(2)}/{t('productDetail.year')}
                        </p>
                      </div>
                    )}
                    <div className="border-t border-slate-200 pt-3 mt-3">
                      {couponResult?.valid && (
                        <>
                          <div className="flex justify-between text-sm text-slate-600 mb-1">
                            <span>{t('products.subtotal')}</span>
                            <span>${orderCalculation.total?.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm text-[#00C48C] font-medium mb-2">
                            <span className="flex items-center gap-1">
                              <Tag className="w-3.5 h-3.5" />
                              {couponCode.toUpperCase()} (-{couponResult.coupon.discount_type === 'percentage' ? `${couponResult.coupon.discount_value}%` : `$${couponResult.coupon.discount_value}`})
                            </span>
                            <span>-${getDiscountAmount().toFixed(2)}</span>
                          </div>
                        </>
                      )}
                      <div className="flex justify-between items-center">
                        <span className="font-heading text-lg font-semibold text-slate-900">{t('products.total')}</span>
                        <span className="font-heading text-2xl font-bold text-[#0A2540]">
                          ${getDiscountedTotal().toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Coupon Code */}
              {orderCalculation && (
                <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6" data-testid="coupon-section">
                  <h3 className="font-heading text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                    <Tag className="w-4 h-4" />
                    {t('checkout.promoCode')}
                  </h3>
                  {couponResult?.valid ? (
                    <div className="flex items-center justify-between bg-[#00C48C]/5 border border-[#00C48C]/20 rounded-xl px-4 py-3">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#00C48C]" />
                        <span className="font-medium text-sm text-[#0A2540]">{couponCode.toUpperCase()}</span>
                        <span className="text-xs text-[#00C48C]">
                          -{couponResult.coupon.discount_type === 'percentage' ? `${couponResult.coupon.discount_value}%` : `$${couponResult.coupon.discount_value}`}
                        </span>
                      </div>
                      <button onClick={removeCoupon} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="remove-coupon-btn">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Input
                        value={couponCode}
                        onChange={(e) => { setCouponCode(e.target.value); if (couponResult) setCouponResult(null); }}
                        placeholder={t('checkout.promoPlaceholder')}
                        className="flex-1"
                        data-testid="coupon-input"
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), applyCoupon())}
                      />
                      <Button
                        type="button"
                        onClick={applyCoupon}
                        disabled={couponApplying || !couponCode.trim()}
                        className="bg-[#0A2540] text-white hover:bg-[#0A2540]/90 rounded-lg px-5"
                        data-testid="apply-coupon-btn"
                      >
                        {couponApplying ? <Loader2 className="w-4 h-4 animate-spin" /> : t('checkout.apply')}
                      </Button>
                    </div>
                  )}
                  {couponResult && !couponResult.valid && (
                    <p className="text-xs text-red-500 mt-2 flex items-center gap-1" data-testid="coupon-error">
                      <XCircle className="w-3.5 h-3.5" />
                      {couponResult.message}
                    </p>
                  )}
                </div>
              )}

              {/* Payment Mode */}
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6">
                <h3 className="font-heading text-base font-semibold text-slate-900 mb-4">
                  {t('products.paymentMode')}
                </h3>
                <div className="space-y-3">
                  <button
                    onClick={() => setPaymentMode('stripe')}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                      paymentMode === 'stripe' ? 'border-[#00C48C] bg-[#00C48C]/5' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="payment-stripe"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                        paymentMode === 'stripe' ? 'border-[#00C48C] bg-[#00C48C]' : 'border-slate-300'
                      }`}>
                        {paymentMode === 'stripe' && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-[#00C48C]" />
                          <span className="font-medium text-slate-900">{t('products.stripePayment')}</span>
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">{t('products.recommended')}</span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{t('products.stripeDesc')}</p>
                      </div>
                    </div>
                  </button>
                  <button
                    onClick={() => setPaymentMode('manual')}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                      paymentMode === 'manual' ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                    data-testid="payment-manual"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mt-0.5 ${
                        paymentMode === 'manual' ? 'border-amber-500 bg-amber-500' : 'border-slate-300'
                      }`}>
                        {paymentMode === 'manual' && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-amber-600" />
                          <span className="font-medium text-slate-900">{t('products.manualPayment')}</span>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{t('products.manualDesc')}</p>
                      </div>
                    </div>
                  </button>
                </div>
              </div>

              {/* Checkout Button / Form */}
              {!showCheckout ? (
                <Button
                  onClick={() => setShowCheckout(true)}
                  className={`w-full h-14 text-lg rounded-full shadow-lg bg-gradient-to-r ${accent} text-white hover:opacity-90`}
                  disabled={needsSigle() && !isSigleValid()}
                  data-testid="proceed-checkout-btn"
                >
                  <ShoppingCart className="w-5 h-5 mr-2" />
                  {paymentMode === 'stripe' ? t('products.payNow') : t('products.submitOrder')}
                </Button>
              ) : (
                <motion.form
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 space-y-4"
                  onSubmit={handleCheckout}
                >
                  <h3 className="font-heading text-base font-semibold text-slate-900 mb-2">
                    {paymentMode === 'stripe' ? t('products.contactInfo') : t('products.createAccount')}
                  </h3>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label htmlFor="firstName">{t('auth.firstName')}</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)}
                          className={`pl-10 ${errors.firstName ? 'border-red-500' : ''}`} placeholder="John"
                          data-testid="first-name-input" />
                      </div>
                      {errors.firstName && <p className="text-red-500 text-xs">{errors.firstName}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="lastName">{t('auth.lastName')}</Label>
                      <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)}
                        className={errors.lastName ? 'border-red-500' : ''} placeholder="Doe"
                        data-testid="last-name-input" />
                      {errors.lastName && <p className="text-red-500 text-xs">{errors.lastName}</p>}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="email">{t('auth.email')}</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                        className={`pl-10 ${errors.email ? 'border-red-500' : ''}`} placeholder="you@example.com"
                        data-testid="email-input" />
                    </div>
                    {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="password">{t('auth.password')}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input id="password" type={showPassword ? 'text' : 'password'} value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`pl-10 pr-10 ${errors.password ? 'border-red-500' : ''}`} placeholder="••••••••"
                        data-testid="password-input" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {errors.password && <p className="text-red-500 text-xs">{errors.password}</p>}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="confirmPassword">{t('auth.confirmPassword')}</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input id="confirmPassword" type={showPassword ? 'text' : 'password'} value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`pl-10 ${errors.confirmPassword ? 'border-red-500' : ''}`} placeholder="••••••••"
                        data-testid="confirm-password-input" />
                    </div>
                    {errors.confirmPassword && <p className="text-red-500 text-xs">{errors.confirmPassword}</p>}
                  </div>

                  {paymentMode === 'manual' && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                      <p className="text-sm text-amber-800">
                        <strong>{t('products.manualPaymentNote')}:</strong> {t('products.manualPaymentWarning')}
                      </p>
                    </div>
                  )}

                  <Button
                    type="submit"
                    className={`w-full h-12 text-lg rounded-full bg-gradient-to-r ${accent} text-white hover:opacity-90`}
                    disabled={submitting || (needsSigle() && !isSigleValid())}
                    data-testid="submit-order-btn"
                  >
                    {submitting ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t('products.processing')}</>
                    ) : paymentMode === 'stripe' ? (
                      <><CreditCard className="w-5 h-5 mr-2" />{t('products.payNow')} {orderCalculation && `$${getDiscountedTotal().toFixed(2)}`}</>
                    ) : (
                      <><ShoppingCart className="w-5 h-5 mr-2" />{t('products.submitOrder')} {orderCalculation && `$${getDiscountedTotal().toFixed(2)}`}</>
                    )}
                  </Button>

                  <TrustBadges t={t} />

                  <button type="button" onClick={() => setShowCheckout(false)}
                    className="w-full text-slate-500 hover:text-slate-700 text-sm">
                    {t('common.cancel')}
                  </button>
                </motion.form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
